import i18next, { type TFunction } from 'i18next';
import { useSyncExternalStore } from 'react';

import type { Lang } from '../api/types.ts';
import { en } from './en.ts';
import { ur } from './ur.ts';

const STORAGE_KEY = 'expense.lang';

function storedLang(): Lang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'ur' ? 'ur' : 'en';
  } catch {
    return 'en';
  }
}

void i18next.init({
  resources: { en: { translation: en }, ur: { translation: ur } },
  lng: storedLang(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  initAsync: false,
});

function applyDocumentLang(lang: Lang): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ur' ? 'rtl' : 'ltr';
}
applyDocumentLang(i18next.language as Lang);

/** {{place}} / {{places}} / {{Place}} / {{Places}} in any text: the app's word for a company,
 * from the Owner role's name (src/lib/terms.ts). Set once the signed-in person is known. */
export function setPlaceWords(words: Record<'place' | 'places' | 'Place' | 'Places', string>): void {
  i18next.options.interpolation = { ...i18next.options.interpolation, defaultVariables: { ...words } };
}

export function setLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private mode: language still switches for this visit */
  }
  void i18next.changeLanguage(lang);
  applyDocumentLang(lang);
}

function subscribe(cb: () => void): () => void {
  i18next.on('languageChanged', cb);
  return () => i18next.off('languageChanged', cb);
}

/** Current language plus a translate function; re-renders when the language changes. */
export function useT(): { t: TFunction; lang: Lang } {
  const lang = useSyncExternalStore(subscribe, () => i18next.language as Lang);
  return { t: i18next.getFixedT(lang), lang };
}
